import { useState, useEffect } from 'react';
import { Modal } from '../modal';
import { Button } from '../button';
import { TextField } from '../text-field';
import { Select } from '../select';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { editProject } from '../../api/data';
import { System } from '../../@types/wells/types';
import { AnalyzeType } from '../../@types/projects/types';
import styles from './EditProjectModal.module.css';

export interface EditProjectModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LAB_SYSTEM_OPTIONS = [
  { value: System.AIRBRINE, label: 'Air-Brine' },
  { value: System.AIROIL,   label: 'Air-Oil' },
  { value: System.OILBRINE, label: 'Oil-Brine' },
  { value: System.AIRHG,    label: 'Air-Hg' },
];

const ANALYZE_TYPE_OPTIONS = [
  { value: AnalyzeType.LIAO,   label: 'LIAO' },
  { value: AnalyzeType.LAMBDA, label: 'LAMBDA' },
];

const EditProjectModal = ({ isModalOpen, setIsModalOpen }: EditProjectModalProps) => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();

  const project = selectedInfo.project;

  const [projectName, setProjectName] = useState(project?.project_name ?? '');
  const [country,     setCountry]     = useState(project?.country     ?? '');
  const [block,       setBlock]       = useState(project?.block       ?? '');
  const [reservoir,   setReservoir]   = useState(project?.reservoir   ?? '');
  const [rockType,    setRockType]    = useState(project?.rock_type   ?? '');
  const [labSystem,   setLabSystem]   = useState<string>(project?.lab_system ?? System.AIRBRINE);
  const [analyzeType, setAnalyzeType] = useState<string>(project?.type_of_analyze ?? AnalyzeType.LIAO);
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    if (isModalOpen && project) {
      setProjectName(project.project_name ?? '');
      setCountry(project.country     ?? '');
      setBlock(project.block         ?? '');
      setReservoir(project.reservoir ?? '');
      setRockType(project.rock_type  ?? '');
      setLabSystem(project.lab_system     ?? System.AIRBRINE);
      setAnalyzeType(project.type_of_analyze ?? AnalyzeType.LIAO);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, project?.uid]);

  const handleSave = async () => {
    if (!project) return;
    setLoading(true);
    try {
      const response = await editProject({
        project_uid:     project.uid,
        project_name:    projectName,
        country:         country   || undefined,
        block:           block     || undefined,
        reservoir:       reservoir || undefined,
        rock_type:       rockType  || undefined,
        lab_system:      labSystem as System,
        type_of_analyze: analyzeType as AnalyzeType,
      });
      upsertProject(response);
      setSelectedInfo({ project: response });
      setIsModalOpen(false);
    } catch (error) {
      console.error('ERROR WHILE EDITING PROJECT:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setIsModalOpen(false);

  return (
    <Modal
      open={isModalOpen}
      onClose={handleClose}
      title="Edit Project"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={loading}>
            Save Project
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <TextField
          label="Project Name"
          fullWidth
          value={projectName}
          onChange={e => setProjectName(e.target.value)}
        />

        <div className={styles.row}>
          <TextField
            label="Country"
            fullWidth
            value={country}
            onChange={e => setCountry(e.target.value)}
          />
          <TextField
            label="Block"
            fullWidth
            value={block}
            onChange={e => setBlock(e.target.value)}
          />
        </div>

        <div className={styles.row}>
          <TextField
            label="Reservoir"
            fullWidth
            value={reservoir}
            onChange={e => setReservoir(e.target.value)}
          />
          <TextField
            label="Rock Type"
            fullWidth
            value={rockType}
            onChange={e => setRockType(e.target.value)}
          />
        </div>

        <div className={styles.row}>
          <Select
            label="Lab System"
            fullWidth
            options={LAB_SYSTEM_OPTIONS}
            value={labSystem}
            onChange={val => setLabSystem(val)}
          />
          <Select
            label="Analysis Type"
            fullWidth
            options={ANALYZE_TYPE_OPTIONS}
            value={analyzeType}
            onChange={val => setAnalyzeType(val)}
          />
        </div>
      </div>
    </Modal>
  );
};

export { EditProjectModal };
